import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Button,
  Divider,
  IconButton,
  InputLabel,
  Stack,
  Tooltip,
} from "@mui/material";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { DialogWindow } from "./DialogWindow";
import { FormTextField } from "UI/FormTextField";
import { getPasswordConfirmValidation, passwordValidation } from "utils";
import { Confirmation } from "./Confirmation";
import { ReactComponent as HelpIcon } from "images/help-icon.svg";

/** @type {import("types/commonTypes").NewProviderData}*/
const defaultFormData = {
  newPassword: "",
  confirmPassword: "",
};

export const SetPasswordDialog = ({
  handleClose,
  dashboardAccessToken,
  email,
}) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const [error, setError] = useState(null);

  const {
    control,
    watch,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm({
    defaultValues: defaultFormData,
  });

  const password = useRef("");
  password.current = watch("newPassword", "");

  const forceCloseAndReset = () => {
    reset(defaultFormData);
    handleClose();
  };

  const confirmedClose = () => {
    if (isDirty) {
      setConfirmation({
        title: "You have unsaved changes",
        message: "Are you sure you want to close this dialog?",
        proceedFn: () => forceCloseAndReset(),
        cancelFn: () => setConfirmation(null),
      });
      return;
    }
    forceCloseAndReset();
  };

  const handleSkip = () => {
    forceCloseAndReset();
  };

  const onSubmit = async (data) => {
    setIsUpdating(true);
    try {
      const { newPassword } = data;

      const response = await fetch(
        `${process.env.REACT_APP_API_V2_URL}/auth/set-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            password: newPassword,
            dashboardAccessToken,
          }),
        }
      );
      const result = await response.json();
      if (response?.status !== 200 || result?.error) {
        setError(result?.message || result?.error);
        return;
      }

      window.localStorage.removeItem(`${email}-is-password-not-set`);
      handleClose();
    } catch (error) {
      console.error(error);
      setError(error?.message ?? "Failed to create new password");
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <DialogWindow
      open={true}
      withCloseButton
      handleClose={confirmedClose}
      title={"Create a Password"}
      content={
        <>
          <form onSubmit={handleSubmit(onSubmit)} noValidate autoComplete="off">
            <Stack mt={2} gap={2}>
              <Stack direction={"column"} gap={2}>
                <Divider />
                <Stack alignItems={"flex-start"}>
                  <div className="flex flex-row justify-start items-center gap-1">
                    <InputLabel htmlFor="newPassword">
                      Type New Password
                    </InputLabel>
                    <Tooltip title="Password must contain at least 8 characters, one lowercase character, one uppercase character, one number, and one special character (e.g “!@#$%^&*)">
                      <HelpIcon width={18} />
                    </Tooltip>
                  </div>
                  <FormTextField
                    id="newPassword"
                    name="newPassword"
                    control={control}
                    rules={passwordValidation}
                    type={isPasswordVisible ? "text" : "password"}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <IconButton
                            color="info"
                            onClick={() =>
                              setIsPasswordVisible(!isPasswordVisible)
                            }
                          >
                            {isPasswordVisible ? (
                              <VisibilityOffIcon />
                            ) : (
                              <VisibilityIcon />
                            )}
                          </IconButton>
                        ),
                      },
                    }}
                  />
                </Stack>
                <Stack alignItems={"flex-start"}>
                  <InputLabel htmlFor="confirmPassword">
                    Repeat New Password
                  </InputLabel>
                  <FormTextField
                    id="confirmPassword"
                    name="confirmPassword"
                    control={control}
                    rules={getPasswordConfirmValidation(password.current)}
                    type={isPasswordVisible ? "text" : "password"}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <IconButton
                            color="info"
                            onClick={() =>
                              setIsPasswordVisible(!isPasswordVisible)
                            }
                          >
                            {isPasswordVisible ? (
                              <VisibilityOffIcon />
                            ) : (
                              <VisibilityIcon />
                            )}
                          </IconButton>
                        ),
                      },
                    }}
                  />
                </Stack>
              </Stack>
            </Stack>
          </form>
          {error && <div className="text-red-500 mt-6">{error}</div>}
          {confirmation && <Confirmation {...confirmation} />}
        </>
      }
      actions={
        <Stack
          direction={"row"}
          justifyContent={"center"}
          alignItems={"center"}
          width={"100%"}
          gap={2}
          mt={error ? 2 : 5}
          mb={2}
        >
          <Button variant="outlined" onClick={handleSkip} disabled={isUpdating}>
            Skip
          </Button>
          <Button
            disabled={isUpdating}
            variant="contained"
            onClick={handleSubmit(onSubmit)}
          >
            Save New Password
          </Button>
        </Stack>
      }
    />
  );
};
